import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { fetchWithLocale } from "components/utils/Api.jsx";
import { buildLocalePath } from "components/utils/Locale";
import OiCarousel from "components/shared/OiCarousel";

const BLOG_CAROUSEL_BREAKPOINTS = [
  { minWidth: 375, slidesPerView: 1.2 },
  { minWidth: 425, slidesPerView: 1.2 },
  { minWidth: 768, slidesPerView: 2.2 },
  { minWidth: 1024, slidesPerView: 4 },
  { minWidth: 1280, slidesPerView: 5 },
];

function pickBreakpoint(breakpoints, width) {
  const sorted = [...breakpoints].sort((a, b) => a.minWidth - b.minWidth);
  let chosen = sorted[0];
  for (let i = 0; i < sorted.length; i += 1) {
    if (width >= sorted[i].minWidth) chosen = sorted[i];
  }
  return chosen;
}

function hoverTintFromPost(post) {
  const s = `${post?.id ?? ''}:${post?.slug ?? ''}`;
  let x = 0;
  for (let i = 0; i < s.length; i++) {
    x = (x * 31 + s.charCodeAt(i)) >>> 0;
  }
  const k = 12;
  const hue = ((x % k) * (360 / k)) | 0;
  const sat = 66 + ((x >>> 4) % 4) * 6;
  const light = 40 + ((x >>> 8) % 3) * 5;
  return `hsla(${hue}, ${sat}%, ${light}%, 0.14)`;
}

function CarouselHomeBlog(props) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [slidesSkeletons, setSlidesSkeletons] = useState(1);
  const productsSectionRef = useRef(null);

  useLayoutEffect(() => {
    const updateSkeletonCount = () => {
      const el = productsSectionRef.current;
      const raw = el?.clientWidth ?? (typeof window !== 'undefined' ? window.innerWidth : 0);
      const width = el ? raw + 24 : raw;
      const bp = pickBreakpoint(BLOG_CAROUSEL_BREAKPOINTS, width);
      setSlidesSkeletons(Math.max(1, Math.ceil(bp.slidesPerView)));
    };
    updateSkeletonCount();
    const el = productsSectionRef.current;
    let ro;
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(updateSkeletonCount);
      ro.observe(el);
    }
    window.addEventListener('resize', updateSkeletonCount);
    return () => {
      window.removeEventListener('resize', updateSkeletonCount);
      if (ro && el) ro.disconnect();
    };
  }, []);

  useEffect(() => {
    get_posts();
  }, []);

  function get_posts() {
    setLoading(true);
    return fetchWithLocale(`/posts.json${props.limit ? '?limit=' + props.limit : ''}`)
      .then(response => response.json())
      .then(json => {
        const lastPost = json?.data?.last_post;
        const apiPosts = json?.data?.posts || [];
        const mergedPosts = lastPost
          ? [lastPost, ...apiPosts.filter((p) => p.slug !== lastPost.slug)]
          : apiPosts;
        const carouselPosts = props.limit ? mergedPosts.slice(0, props.limit) : mergedPosts;
        setPosts(carouselPosts);
        setLoading(false);
      })
      .catch(error => console.log(error));
  }

  const handleImageError = (e) => {
    e.target.onerror = null;
  };

  return (
      !props.home_collection_id && (
        <section className="posts">
          <header className="collections" key={"home_collection_id" + props.home_collection_title}>
            <h2><b>{props.home_collection_title}</b> {props.home_collection_subtitle}</h2>
            <div className="more_index">
              <a
                className="more h4"
                href={buildLocalePath('blog')}
                target="_blank"
                rel="noopener noreferrer"
              >
                {props.home_collection_link_text}
              </a>
            </div>
          </header>
          <section className="caroussel">
            <section ref={productsSectionRef} className="products-section">
              {loading ? (
                <section className="carousel-skeletons-loader carousel-home__skeleton-row">
                  {Array.from({ length: slidesSkeletons }).map((_, idx) => (
                    <div
                      key={`post-skeleton-${idx}`}
                      className="carousel-home__skeleton-cell--blog-carousel skeletons-loader oi-skeleton oi-skeleton--wave oi-skeleton--rounded"
                      style={{ height: 330 }}
                      aria-hidden
                    />
                  ))}
                </section>
              ) : (
                <OiCarousel
                  className="oi-carousel--bleed"
                  items={posts || []}
                  gap={0}
                  breakpoints={BLOG_CAROUSEL_BREAKPOINTS}
                  ariaLabel="Entradas del blog"
                  getItemKey={(post) => `posts-label-${post.id ?? post.slug}`}
                  renderItem={(post) => (
                    <article className="post-grid">
                      <div
                        className="post-wraper home-blog-card-stack"
                        style={{ '--product-card-hover-overlay': hoverTintFromPost(post) }}
                      >
                        <a
                          className="post-grid-link oi-flex oi-flex-column"
                          href={buildLocalePath(`posts/${post.slug}`)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {post.cover_f && (
                            <figure className="home-blog-carousel-figure">
                              <img
                                className="lazyload"
                                src={post.cover_f}
                                onError={handleImageError}
                                alt={post.title}
                              />
                            </figure>
                          )}
                          <div className="info-slide text-slide-fix oi-flex oi-flex-column">
                            <p className="zone h7">{post.label}</p>
                            <h4>{post.title}</h4>
                          </div>
                        </a>
                      </div>
                    </article>
                  )}
                />
              )}
            </section>
          </section>
        </section>
    )
  );
}

export default CarouselHomeBlog;
